import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { Community } from '~/interfaces/community';
import { NectarPageBlock, nectarPageBlock } from '~/utils/nectarPageBlock';

import { Avatar } from './ui/Avatar';
import { PillButton } from './ui/PillButton';

import { getCommunityDescription } from '../_utils/getCommunityDescription';
import { getFullCommunityName } from '../_utils/getFullCommunityName';
import { GateBannerCard, GateBannerLogo, GateBannerTitle } from './GateBannerCard';

interface JoinBannerProps {
  community: Community | null;
  // Omitted when the community is upgrade-modal-gated with no purchasable plan
  // to offer (COM-118 rule) — the banner copy stays, the CTA disappears.
  onJoin?: () => void;
}

export const JoinBanner: FC<JoinBannerProps> = ({ community, onJoin }) => {
  const { t } = useTranslation();
  const communityName = getFullCommunityName(community);
  const previewMembers = community?.preview_members ?? [];
  const memberCount = community?.member_count ?? 0;
  const description = getCommunityDescription(community, t);

  return (
    <GateBannerCard>
      <GateBannerLogo alt={communityName} />
      <GateBannerTitle>{t('community.join.title', { communityName })}</GateBannerTitle>
      <p className="mt-3 max-w-[360px] text-base leading-6 text-community-text-on-bg/75">{description}</p>
      {memberCount > 0 && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {previewMembers.length > 0 && (
            <div className="flex -space-x-2">
              {previewMembers.map((member) => (
                <Avatar
                  key={member.id}
                  src={member.avatar_url}
                  alt={member.name || t('community.join.former_member')}
                  className="size-8 border-2 border-community-text-on-bg/70 bg-community-text-on-bg/20"
                  fallbackClassName="border-2 border-community-text-on-bg/70 bg-community-text-on-bg/20"
                />
              ))}
            </div>
          )}
          <span className="text-lg font-medium leading-7 text-community-text-on-bg/90">
            {t('community.join.member_count', { count: memberCount })}
          </span>
        </div>
      )}
      {onJoin && (
        <PillButton
          type="button"
          onClick={onJoin}
          variant="primary"
          size="lg"
          className="mt-7 min-w-[168px]"
          {...nectarPageBlock(NectarPageBlock.CommunityJoinBanner)}
        >
          {t('community.join.cta')}
        </PillButton>
      )}
    </GateBannerCard>
  );
};
